import { component, OnInit } from '@gbds/core/src/ts/core'
import { getWindowSize, isVerticallyInViewport } from '../../../utils/windowSize'
import { debounce } from '@gbds/core/src/ts/utils'

// Placeholder for Quantum JS logic

enum responsiveImageConstants {
	fullHeightClass = 'gb-full-height',
	centerClass = 'gb-center-transform',
	lazyLoadAnimationClass = 'gb-animated-fade-in',
	imageContainerSelector = '.gb-responsive-image-container',
	imageSelector = '.gb-image',
	dataEnableHeightControl = 'data-enable-image-height-control',
	imageContainerWhenContentOverlaySelector = '.gb-image-overlay .gb-responsive-image-container',
	dataHeightPrefix = 'data-height-',
	dataHeightDefault = 'data-height-default',
	statImageLinkSelector = '.stat-image-link',
	contentOverlaySelector = '.gb-content-overlay',
	bacIdParam = 'bacid',
}

/**
 * @noInheritDoc
 */

@component('gb-responsive-image')
export class ResponsiveImage extends HTMLElement implements OnInit {
	constructor() {
		super()
	}

	private _imageContainer: HTMLElement
	private _image: HTMLElement
	private _imageLink: HTMLElement
	private _isImageHeightControlEnabled = false
	private _viewed = false
	private _currentWindowSize: string

	onInit(): void {
		this._currentWindowSize = getWindowSize()

		if (this.querySelector(responsiveImageConstants.contentOverlaySelector) != null) {
			this._imageContainer = this.querySelector(responsiveImageConstants.imageContainerWhenContentOverlaySelector)
		} else {
			this._imageContainer = this.querySelector(responsiveImageConstants.imageContainerSelector)
		}

		this._image = this.querySelector(responsiveImageConstants.imageSelector)
		this._imageLink = this.getElementsByTagName('a')[0]

		if (
			this._imageContainer &&
			this._imageContainer.hasAttribute(responsiveImageConstants.dataEnableHeightControl)
		) {
			this._isImageHeightControlEnabled =
				this._imageContainer.getAttribute(responsiveImageConstants.dataEnableHeightControl) == 'true'
		}

		//Binding required so that this refers to the component class instead of window
		//Instead of using onEvent, use add event listener to avoid overriding previous event handler
		window.addEventListener('resize', debounce(this.onBroadcastWindowResize.bind(this), 250))
		window.addEventListener('scroll', debounce(this.onBroadcastWindowScroll.bind(this), 250))
		this._image.addEventListener('load', this.setImageContainerHeight.bind(this))
	}

	setImageContainerHeight(): void {
		if (this._imageContainer == null) {
			return
		}

		const heightControlDefault =
			this._imageContainer.getAttribute(responsiveImageConstants.dataHeightDefault) || '100%'
		const windowSizeHeight = responsiveImageConstants.dataHeightPrefix + getWindowSize()
		const heightControlTarget = this._imageContainer.getAttribute(windowSizeHeight) || heightControlDefault
		const heightFinal = this.formatHeight(heightControlTarget)

		if (this._isImageHeightControlEnabled) {
			if (heightControlTarget.includes('%') && !heightControlTarget.includes('calc')) {
				this._imageContainer.setAttribute('style', 'height: ' + heightFinal + 'px')
			} else {
				this._imageContainer.setAttribute('style', 'height: ' + heightFinal)
			}
			this.onCropImage()
		}
	}

	formatHeight(height: string): string {
		const imageHeight = getComputedStyle(this._image, null).height

		if (('' + height).search(/%$/) > 0) {
			const heightNum = (+height.split('%')[0] / 100) * parseFloat(imageHeight)
			return heightNum + ''
		}

		return height
	}

	onCropImage(): void {
		if (!this._image || !this._imageContainer) {
			return
		}

		//Unary operator used to turn the style string into a number
		const imageHeight = +getComputedStyle(this._image, null).height
		const imageWidth = +getComputedStyle(this._image, null).width
		const imageContainerHeight = +getComputedStyle(this._imageContainer, null).height
		const imageContainerWidth = +getComputedStyle(this._imageContainer, null).width

		if (this._isImageHeightControlEnabled) {
			if (imageHeight < imageContainerHeight) {
				this._image.classList.add(responsiveImageConstants.fullHeightClass)
			} else if (imageWidth < imageContainerWidth) {
				this._image.classList.remove(responsiveImageConstants.fullHeightClass)
			}

			if (imageWidth == imageContainerWidth && imageHeight == imageContainerHeight) {
				this._image.classList.remove(responsiveImageConstants.centerClass)
			} else {
				this._image.classList.add(responsiveImageConstants.centerClass)
			}
		}
	}

	//data-image-lazy-load-enabled = true || false
	checkIfViewableAndAddAnimation(): void {
		const bodyElements = document.getElementsByTagName('body')
		let lazyLoadEnabled = false
		if (bodyElements.item(0).hasAttribute('data-image-lazy-load-enabled')) {
			lazyLoadEnabled = bodyElements.item(0).getAttribute('data-image-lazy-load-enabled') == 'true'
		}

		if (lazyLoadEnabled && !this._viewed) {
			if (isVerticallyInViewport(this)) {
				this.classList.add(responsiveImageConstants.lazyLoadAnimationClass)
				this._viewed = true
			}
		}
	}

	onBreakpointChange(): void {
		this.setImageContainerHeight()
	}

	onBroadcastWindowResize(): void {
		this.onCropImage()
		if (getWindowSize() != this._currentWindowSize) {
			this._currentWindowSize = getWindowSize()
			this.onBreakpointChange()
		}
	}

	onBroadcastWindowScroll(): void {
		this.checkIfViewableAndAddAnimation()
	}
}
